import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowLeft, GraduationCap, Globe2, Award, BookOpen, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navigation from '../components/Navigation';
import EducationBackground from '../components/EducationBackground';
import '../App.css';

gsap.registerPlugin(ScrollTrigger);

const EducationPage = ({ nightMode, setNightMode }) => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const isMobile = window.innerWidth <= 768;
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  const educationData = [
    {
      year: '2025',
      period: 'Aug 2025',
      title: 'Break Into the Fashion Industry',
      institution: 'Glam Observer',
      description: 'A course that explored how influence is built, not through noise, but through intention. It redefined branding for me, both as something you manage and something you become.',
      icon: Sparkles,
      stats: ['Editorial Strategy', 'Luxury Communication', 'Brand Storytelling'],
      logo: '/logos-unis/glam observer.png',
    },
    {
      year: '2024-25',
      period: '2024 - 2025',
      title: 'MSc in Luxury Management & Marketing',
      institution: 'emlyon business school, Paris',
      gpa: 'GPA 4.0/4.0',
      description: 'Paris made learning feel instinctive. emlyon tied together business, culture, and emotion in a way that felt real, how luxury isn’t about selling things, but shaping meaning. Every project and conversation sharpened how I read people, patterns, and the quiet details that move them.',
      icon: GraduationCap,
      logo: '/logos-unis/emlyon.png',
      exchanges: [
        {
          name: 'Study Abroad Exchange Semester - Curtin University, Perth',
          date: 'Apr 2025',
          desc: 'Explored luxury retail, sustainability, and digital storytelling. Australia taught me that quiet precision and ethics are forms of luxury too.',
          logo: '/logos-unis/Curtin University.png',
        },
        {
          name: 'Study Abroad Exchange Semester - London College of Fashion, UAL',
          date: 'Jan 2025',
          desc: 'Studied fashion theory, visual merchandising, and creative direction. London redefined my view of culture - chaos that somehow always lands on trend.',
          logo: '/logos-unis/ual.png',
        },
      ],
      stats: ['Consumer Behavior', 'Sustainability Strategy', 'Digital Transformation'],
    },
    {
      year: '2023',
      period: '2023',
      title: 'French Language Studies',
      institution: 'Alliance Française - A1 Level',
      description: 'Scored 86/100. Reading and understanding come easy; speaking still sounds like an art form.',
      icon: BookOpen,
      stats: ['Reading Proficiency', 'Cultural Immersion'],
      logo: '/logos-unis/alliance francaise.png',
    },
    {
      year: '2018-22',
      period: '2018 - 2022',
      title: 'Bachelor of Fashion Technology',
      institution: 'National Institute of Fashion Technology, India',
      gpa: 'CGPA 8.6/10',
      description: 'Learned the architecture of fashion - apparel engineering, merchandising, and textile innovation. Took UI/UX and Figma classes outside the syllabus, because I have always believed aesthetics should function beautifully too.',
      icon: Award,
      logo: '/logos-unis/NIFT.png',
      stats: ['Apparel Engineering', 'Merchandising', 'Textile Innovation', 'UI/UX Design'],
    },
    {
      year: '2015-17',
      period: '2015 - 2017',
      title: 'Earlier Education',
      institution: 'India',
      description: 'Two years of IIT-JEE preparation taught structure, logic, and persistence. Before that, CBSE schooling shaped the discipline and curiosity I now use in more creative directions.',
      icon: BookOpen,
      stats: ['Analytical Thinking', 'Discipline', 'Foundation'],
      logo: null,
    },
  ];

  useEffect(() => {
    const isMobile = window.innerWidth <= 768;

    // Disable heavy scroll animations on mobile for performance
    if (isMobile) return;

    // Smooth scroll reveal for timeline items
    const items = gsap.utils.toArray('.edu-timeline-item');

    items.forEach((item, index) => {
      gsap.fromTo(item,
        {
          opacity: 0,
          y: 30,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: item,
            start: 'top 90%',
            end: 'top 70%',
            toggleActions: 'play none none none',
            once: true,
          },
        }
      );
    });

    return () => ScrollTrigger.getAll().forEach(st => st.kill());
  }, []);

  return (
    <div className={`edu-page ${nightMode ? 'night' : ''}`} ref={containerRef}>
      <EducationBackground nightMode={nightMode} />

      <div className="edu-content-layer">
        {/* Nav */}
        <div className="edu-nav">
          <motion.button
            className="edu-back"
            onClick={() => navigate('/')}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ x: -5 }}
          >
            <ArrowLeft size={18} />
            <span>Back</span>
          </motion.button>

          <motion.button
            className="edu-theme"
            onClick={() => setNightMode(!nightMode)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {nightMode ? '☀' : '🌙'}
          </motion.button>
        </div>

        {/* Hero */}
        <motion.div
          className="edu-hero"
          initial={isMobile ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: isMobile ? 0.3 : 0.8 }}
        >
          <motion.span
            className="edu-label"
            initial={isMobile ? { opacity: 1 } : { opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: isMobile ? 0.2 : 0.6, delay: 0.1 }}
          >
            EDUCATION
          </motion.span>
          <h1 className="edu-title">
            <motion.span
              className="edu-title-line"
              initial={isMobile ? { opacity: 1 } : { opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: isMobile ? 0.2 : 0.8, delay: 0.2 }}
            >
              Built through intention,
            </motion.span>
            <motion.span
              className="edu-title-line"
              initial={isMobile ? { opacity: 1 } : { opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: isMobile ? 0.2 : 0.8, delay: 0.3 }}
            >
              curiosity, and
            </motion.span>
            <motion.span
              className="edu-title-line edu-title-accent"
              initial={isMobile ? { opacity: 1 } : { opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: isMobile ? 0.2 : 0.8, delay: 0.4 }}
            >
              global perspectives.
            </motion.span>
          </h1>
          <motion.p
            className="edu-subtitle"
            initial={isMobile ? { opacity: 1 } : { opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: isMobile ? 0.2 : 0.8, delay: 0.5 }}
          >
            From NIFT to Paris, London, and Perth - each chapter shaped how I see luxury, culture, and craft.
          </motion.p>
        </motion.div>

        {/* Timeline */}
        <div className="edu-timeline">
          {/* Vertical line */}
          <motion.div
            className="edu-line"
            style={{
              scaleY: isMobile ? 1 : useTransform(scrollYProgress, [0, 1], [0, 1]),
            }}
          />

          {/* Items */}
          {educationData.map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={index} className="edu-timeline-item">
                {/* Left: Content Card */}
                <div className="edu-content">
                  <div className="edu-card">
                    {/* University Logo Badge */}
                    {item.logo && (
                      <div className="edu-logo-badge">
                        <img src={item.logo} alt={item.institution} className="edu-logo" />
                      </div>
                    )}

                    <div className="edu-card-header">
                      <div className="edu-icon">
                        <Icon size={20} strokeWidth={2} />
                      </div>
                      <div className="edu-period">{item.period}</div>
                    </div>

                    <h3 className="edu-card-title">{item.title}</h3>
                    <div className="edu-inst">{item.institution}</div>
                    {item.gpa && <div className="edu-gpa">{item.gpa}</div>}
                    <p className="edu-desc">{item.description}</p>

                    {/* Exchanges */}
                    {item.exchanges && (
                      <div className="edu-exchanges">
                        <div className="ex-header">
                          <Globe2 size={13} strokeWidth={2.5} />
                          <span>Exchange Programs</span>
                        </div>
                        {item.exchanges.map((ex, idx) => (
                          <div key={idx} className="ex-item">
                            {ex.logo && (
                              <div className="ex-logo-wrapper">
                                <img src={ex.logo} alt={ex.name} className="ex-logo" />
                              </div>
                            )}
                            <div className="ex-content">
                              <div className="ex-name">{ex.name}</div>
                              <div className="ex-date">{ex.date}</div>
                              <p className="ex-desc">{ex.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Stats */}
                    {item.stats && (
                      <div className="edu-stats">
                        {item.stats.map((stat, idx) => (
                          <span key={idx} className="stat-tag">
                            {stat}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Connecting line from card to node */}
                  <div className="edu-connector"></div>
                </div>

                {/* Center: Timeline Node */}
                <div className="edu-node-wrapper">
                  <div className="edu-node"></div>
                </div>

                {/* Right: Year */}
                <div className="edu-year-wrapper">
                  <div className="edu-year">{item.year}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <motion.div
          className="edu-footer"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <p>
            Every course, exchange, and essay shaped how I see luxury<br />
            but as culture, emotion, and precision.
          </p>
        </motion.div>

        {/* Navigation */}
        <Navigation activeSection="archive" onNavigate={(section) => navigate('/')} nightMode={nightMode} />
      </div>
    </div>
  );
};

export default EducationPage;
